import {
  Goal,
  GroupByMode,
  ID,
  ReorderSectionUpdate,
  SortingMode,
  UpdateGoal,
} from '@griply/types';

import {
  buildCustomTaskOrderReorderPatch,
  buildSectionReorderPatch,
  withCustomSortFilterDocument,
} from './build-linked-item-reorder-patch';

export type BuildGoalReorderUpdateOptions = {
  goal: Goal;
  subGoals: Goal[];
  updatedSections: ReorderSectionUpdate[];
  groupBy: GroupByMode | undefined | null;
  // The root pseudo-section carries the section order (Section mode) or the sub-goal order (Goal mode).
  rootSectionId: ID;
  // In Section mode the "no section" bucket carries the flat taskSorting.
  noGroupingSectionId: ID;
};

export const buildGoalReorderUpdate = ({
  goal,
  subGoals,
  updatedSections,
  groupBy,
  rootSectionId,
  noGroupingSectionId,
}: BuildGoalReorderUpdateOptions): UpdateGoal[] => {
  const groupByMode = groupBy ?? GroupByMode.Section;

  if (groupByMode === GroupByMode.Section) {
    return [
      buildSectionReorderPatch({
        container: goal,
        updatedSections,
        rootSectionId,
        noGroupingSectionId,
      }),
    ];
  }

  if (groupByMode === GroupByMode.Goal) {
    const updates: UpdateGoal[] = [];

    updatedSections.forEach((section) => {
      if (section.id === rootSectionId) {
        updates.push({ id: goal.id, goalSorting: section.taskSorting });
        return;
      }

      if (section.id === goal.id) {
        updates.push({
          id: goal.id,
          taskSorting: section.taskSorting,
          taskSortingMode: SortingMode.Custom,
          filterDocument: withCustomSortFilterDocument(goal),
        });
        return;
      }

      const subGoal = subGoals.find((g) => g.id === section.id);
      if (subGoal) {
        updates.push({
          id: subGoal.id,
          taskSorting: section.taskSorting,
          taskSortingMode: SortingMode.Custom,
          filterDocument: withCustomSortFilterDocument(subGoal),
        });
      }
    });

    return updates;
  }

  const patch = buildCustomTaskOrderReorderPatch({
    container: goal,
    updatedSections,
    groupByMode,
    rootSectionId,
  });
  return patch ? [patch] : [];
};
